import { isEmpty, reduce } from 'lodash';
import browserCookies from 'browser-cookies';
import cookie from 'cookie';
import Debug from 'debug';
import nanoid from 'nanoid';

import PermanentStorage from '../permanentStorage';

const debug = Debug('rs:utils/auth');

const KEY_COOKIE_TOKEN = 'token';
const KEY_STORAGE = 'auth';
const KEY_STATE = `${KEY_STORAGE}.state`;
const ONE_YEAR = 1000 * 60 * 60 * 24 * 365;

/**
 * Parses an OAuth fragment response into an object.
 * Essentially, converts string key-value pairs into an object.
 *
 * @params {string} resp - the response string
 * @returns {Object|null} - Returns null if response could not be parsed.
 */
const parseOauthFragment = (resp = '') => {
  const str = resp.startsWith('#') ? resp.substr(1) : resp;
  const pairs = str.split('&');

  if (pairs.length < 0) {
    return null;
  }

  return reduce(
    pairs,
    (memo, pair) => {
      // Avoid mangling access_token, which can contain the "=" character.
      const parts = pair.split('=', 2);
      return {
        ...memo,
        [parts[0]]: parts[1],
      };
    },
    {},
  );
};

const Auth = {
  /**
   * Initiates authentication by providing the URL to which the user should
   * be redirected. When handling the callback URL, the caller should verify
   * the response in the URL fragment.
   *
   * @params {Object} options - all required.
   *
   *  - authUrl: [string] The URL to initiate an OAuth login request.
   *
   *  - callbackUrl: [string] The next URL when the Oauth login request ends.
   *
   *  - clientId: [string] The client ID issued by the OAuth server.
   *
   * @returns {Promise} - when resolved, will pass the assembled loginURL as
   *  the first argument.
   */
  initiate({ authUrl, callbackUrl, clientId }) {
    return new Promise((resolve, reject) => {
      try {
        const state = nanoid();

        PermanentStorage.set(KEY_STATE, state); // verified on callback

        const params = [
          `client_id=${clientId}`,
          `redirect_uri=${encodeURIComponent(callbackUrl)}`,
          'response_type=token',
          `state=${state}`,
        ];

        const loginUrl = `${authUrl}?${params.join('&')}`;
        resolve(loginUrl);
      } catch (err) {
        // Using this try-catch block ensures errors involving bad or missing
        // options values are bubbled up for the caller to handle.
        reject(err);
      }
    });
  },

  /**
   * Verifies the received callback response. Returns a promise that rejects
   * on failed verification. When fulfilled, it passes an object of key-value
   * pairs from the response.
   *
   * @param {string} resp - the response string
   * @returns {Promise}
   */
  verifyCallback(resp = '') {
    return new Promise((resolve, reject) => {
      debug('verify callback');

      const data = parseOauthFragment(resp);

      if (isEmpty(data)) {
        reject(new Error('failed to parse response'));
        return;
      }

      // Verify state.
      if (data.state !== PermanentStorage.get(KEY_STATE)) {
        reject(new Error('state verification failed'));
        return;
      }

      // Check for response errors.
      if (data.error !== undefined) {
        reject(new Error(data.error_description || data.error));
        return;
      }

      resolve(data);
    });
  },

  setToken(value) {
    debug('set token');
    browserCookies.set(KEY_COOKIE_TOKEN, value, this.makeTokenCookieOptions());
  },

  setTokenResponseCookie(res, token) {
    debug('setting token cookie to response');
    res.setHeader('Set-Cookie', [
      [
        cookie.serialize(
          KEY_COOKIE_TOKEN,
          token,
          this.makeTokenCookieOptions(),
        ),
      ],
    ]);
  },

  makeTokenCookieOptions() {
    return {
      path: '/',
      expires: new Date(Date.now() + ONE_YEAR),
    };
  },

  getToken() {
    debug('get token');
    const token = browserCookies.get(KEY_COOKIE_TOKEN);
    if (token) debug('has token');
    return token;
  },

  clearToken() {
    debug('clear token');
    browserCookies.erase(KEY_COOKIE_TOKEN);
    if (!this.getToken()) debug('token cleared');
  },

  clear() {
    debug('clear storage');
    [KEY_STATE, KEY_STORAGE].forEach(PermanentStorage.remove);
  },
};

export default Auth;
